import { useEffect, useState } from "react";
import { TrendingUp, Users, Target, BarChart3 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Progress } from "./ui/progress";
import { Skeleton } from "./ui/skeleton";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { useAuth } from "@/contexts/AuthContext";
import { apiGetDashboardStats } from "@/lib/api";
import { EmptyState } from "./EmptyState";
import type { DashboardStats } from "@/lib/api";

const CHART_COLORS = ['#E11D48', '#8B5CF6', '#64748B', '#F59E0B']

export function HeroPanel() {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const data = await apiGetDashboardStats();
        if (!cancelled) setStats(data);
      } catch (e: unknown) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true };
  }, [reloadTick]);

  const firstName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || 'utilisateur';

  if (loading) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-[380px] rounded-xl" />
          <Skeleton className="h-[380px] rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon="⚠️"
        title="Impossible de charger le tableau de bord"
        description={error}
        secondaryLabel="Réessayer"
        onSecondaryClick={() => setReloadTick((t) => t + 1)}
      />
    );
  }

  const hasAnyData =
    !!stats &&
    (stats.activeCampaignCount > 0 || stats.supplierCount > 0 || stats.monthlyData.some((m) => Object.keys(m).length > 1));

  if (!hasAnyData) {
    return (
      <div className="space-y-4">
        <h1 className="text-3xl font-bold text-foreground">Bienvenue, {firstName}</h1>
        <EmptyState
          icon="📊"
          title="Aucune campagne active"
          description="Créez votre première campagne pour commencer à collecter les données de vos fournisseurs."
          primaryLabel="Demander à l'agent"
        />
      </div>
    );
  }

  // Type-narrowed : hasAnyData garantit que stats est non-null
  const statsData = stats!;

  const pieData = statsData.campaignProgress.map((c, i) => ({
    id: c.id,
    name: c.name,
    value: c.submitted,
    color: CHART_COLORS[i % CHART_COLORS.length],
  }));

  const responseRateLabel = statsData.responseRate === null ? '—' : `${statsData.responseRate}%`;

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="flex flex-col space-y-4 p-8 rounded-2xl bg-primary/5 border border-primary/20 shadow-lg">
        <div className="flex items-center space-x-4">
          <div className="h-16 w-16 rounded-xl bg-primary/10 flex items-center justify-center">
            <span className="text-3xl">🌍</span>
          </div>
          <div>
            <h1 className="text-3xl font-bold text-foreground">Bienvenue, {firstName}</h1>
            <p className="text-muted-foreground text-lg">
              Pilotez l'impact environnemental grâce à la collaboration fournisseurs et aux analyses ACV.
            </p>
          </div>
        </div>
        <div className="flex items-center space-x-6 text-sm">
          <div className="flex items-center space-x-2 text-primary font-medium">
            <Target className="h-5 w-5" />
            <span>{statsData.activeCampaignCount} campagne{statsData.activeCampaignCount > 1 ? 's' : ''} active{statsData.activeCampaignCount > 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center space-x-2 font-medium text-violet-500">
            <Users className="h-5 w-5" />
            <span>{statsData.supplierCount} fournisseur{statsData.supplierCount > 1 ? 's' : ''} engagé{statsData.supplierCount > 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center space-x-2 text-primary font-medium">
            <TrendingUp className="h-5 w-5" />
            <span>{responseRateLabel} taux de réponse</span>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card className="border-primary/20 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Campagnes actives</CardTitle>
            <Target className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{statsData.activeCampaignCount}</div>
            <p className="text-xs text-muted-foreground">Toutes catégories</p>
          </CardContent>
        </Card>

        <Card className="border-primary/20 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Fournisseurs engagés</CardTitle>
            <Users className="h-4 w-4 text-violet-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-violet-500">{statsData.supplierCount}</div>
            <p className="text-xs text-muted-foreground">Toutes campagnes</p>
          </CardContent>
        </Card>

        <Card className="border-primary/20 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Taux de réponse</CardTitle>
            <TrendingUp className="h-4 w-4 text-primary" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{responseRateLabel}</div>
            <p className="text-xs text-muted-foreground">Campagnes actives</p>
          </CardContent>
        </Card>

        <Card className="border-primary/20 shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Campagnes suivies</CardTitle>
            <BarChart3 className="h-4 w-4 text-slate-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-slate-500">{statsData.campaignProgress.length}</div>
            <p className="text-xs text-muted-foreground">En cours de suivi</p>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      {statsData.campaignProgress.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2">
          <Card className="shadow-md">
            <CardHeader>
              <CardTitle>Tendances des réponses</CardTitle>
              <CardDescription>Réponses mensuelles par campagne</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={statsData.monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    {statsData.campaignProgress.map((campaign, i) => (
                      <Line
                        key={campaign.id}
                        type="monotone"
                        dataKey={campaign.id}
                        name={campaign.name}
                        stroke={CHART_COLORS[i % CHART_COLORS.length]}
                        strokeWidth={3}
                        dot={{ fill: CHART_COLORS[i % CHART_COLORS.length] }}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-md">
            <CardHeader>
              <CardTitle>Répartition des campagnes</CardTitle>
              <CardDescription>Réponses reçues par campagne</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={120}
                      paddingAngle={5}
                      dataKey="value"
                      nameKey="name"
                    >
                      {pieData.map((entry) => (
                        <Cell key={entry.id} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-1 gap-2 mt-4">
                {pieData.map((item) => (
                  <div key={item.id} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-sm text-muted-foreground">{item.name}</span>
                    </div>
                    <span className="text-sm font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Progress Section */}
      {statsData.campaignProgress.length > 0 && (
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Progression des campagnes</CardTitle>
            <CardDescription>Suivi de progression des campagnes actives</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {statsData.campaignProgress.map((campaign) => (
              <div key={campaign.id} className="space-y-2">
                <div className="flex justify-between">
                  <div>
                    <span className="font-medium">{campaign.name}</span>
                    <p className="text-xs text-muted-foreground">Objectif : {campaign.total} fournisseurs</p>
                  </div>
                  <span className="font-medium">{campaign.submitted} / {campaign.total}</span>
                </div>
                <Progress value={campaign.progress} className="h-2" />
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
